// src/components/lists/BeingsList/BeingsFilterDrawer.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as FilterListIcon,
} from '@mui/icons-material';
import { BeingType, FilterState } from '../../../hooks/useBeingsFilter';
import { PetType } from '../../../services/api';
import { styled } from '@mui/material/styles';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  beingType: BeingType;
  filterState: FilterState;
  onApply: (drawerFilters: Partial<FilterState>) => void;
  onClear: () => void;
  petTypes?: PetType[];
}

const AccordionSectionTitle = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: '1.6rem',
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  '& .MuiAccordionSummary-content': { margin: '4px 0' },
  '& .MuiAccordionSummary-content.Mui-expanded': { margin: '4px 0' },
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
  padding: '1px 3px 1px 12px',
  fontWeight: 700,
  fontSize: '1.6rem',
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '& .MuiTypography-root': {
    fontSize: '1.6rem',
    fontWeight: 700,
    lineHeight: 1.1,
    margin: 0,
    fontFamily: theme.typography.fontFamily,
  },
}));

type DrawerFilterState = Pick<
  FilterState,
  | 'hasPets'
  | 'hasBookings'
  | 'hasOverdueInvoices'
  | 'hasOutstandingBalance'
  | 'stillOwned'
  | 'petTypeIds'
  | 'sortBy'
  | 'sortOrder'
>;

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  beingType,
  filterState,
  onApply,
  onClear,
  petTypes = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [localState, setLocalState] = useState<DrawerFilterState>({});

  // Re-initialize local state from committed filter state each time drawer opens.
  useEffect(() => {
    if (open) {
      setLocalState({
        hasPets: filterState.hasPets,
        hasBookings: filterState.hasBookings,
        hasOverdueInvoices: filterState.hasOverdueInvoices,
        hasOutstandingBalance: filterState.hasOutstandingBalance,
        stillOwned: filterState.stillOwned,
        petTypeIds: filterState.petTypeIds,
        sortBy: filterState.sortBy,
        sortOrder: filterState.sortOrder ?? 'asc',
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocal = (key: keyof DrawerFilterState, value: any) => {
    setLocalState((prev) => ({ ...prev, [key]: value }));
  };

  const handlePetTypeToggle = (typeId: number) => {
    const current = localState.petTypeIds || [];
    const updated = current.includes(typeId)
      ? current.filter((id) => id !== typeId)
      : [...current, typeId];
    setLocal('petTypeIds', updated.length > 0 ? updated : undefined);
  };

  const handleApply = () => {
    onApply(localState);
    onClose();
  };

  const handleClear = () => {
    setLocalState({ sortOrder: 'asc' });
    onClear();
    onClose();
  };

  const showClientFilters = beingType === 'clients';
  const showPetFilters = beingType === 'pets' || beingType === 'clientPets';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '80%' : 400,
          backgroundColor: theme.palette.background.default,
          borderLeft: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.primary.main
                : theme.palette.primary.main,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: theme.palette.grey[50] }} />
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize="2rem"
              sx={{ color: theme.palette.grey[50] }}
            >
              Filters
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: theme.palette.grey[50] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
          {showClientFilters && (
            <>
              <Accordion defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <AccordionSectionTitle>Pets</AccordionSectionTitle>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <RadioGroup
                    value={
                      localState.hasPets === undefined
                        ? 'all'
                        : localState.hasPets
                          ? 'yes'
                          : 'no'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setLocal(
                        'hasPets',
                        v === 'all' ? undefined : v === 'yes'
                      );
                    }}
                  >
                    <StyledFormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All clients"
                    />
                    <StyledFormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Has pets"
                    />
                    <StyledFormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No pets"
                    />
                  </RadioGroup>
                </StyledAccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <AccordionSectionTitle>Bookings</AccordionSectionTitle>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <RadioGroup
                    value={
                      localState.hasBookings === undefined
                        ? 'all'
                        : localState.hasBookings
                          ? 'yes'
                          : 'no'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setLocal(
                        'hasBookings',
                        v === 'all' ? undefined : v === 'yes'
                      );
                    }}
                  >
                    <StyledFormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All clients"
                    />
                    <StyledFormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Has bookings"
                    />
                    <StyledFormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No bookings"
                    />
                  </RadioGroup>
                </StyledAccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <AccordionSectionTitle>Account</AccordionSectionTitle>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <RadioGroup
                    value={
                      localState.hasOverdueInvoices === undefined
                        ? 'all'
                        : localState.hasOverdueInvoices
                          ? 'yes'
                          : 'no'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setLocal(
                        'hasOverdueInvoices',
                        v === 'all' ? undefined : v === 'yes'
                      );
                    }}
                  >
                    <StyledFormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All clients"
                    />
                    <StyledFormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Has overdue invoices"
                    />
                    <StyledFormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No overdue invoices"
                    />
                  </RadioGroup>

                  <Divider
                    sx={{
                      my: 1,
                      border: `1px solid ${theme.palette.grey[300]}`,
                    }}
                  />

                  <RadioGroup
                    value={
                      localState.hasOutstandingBalance === undefined
                        ? 'all'
                        : localState.hasOutstandingBalance
                          ? 'yes'
                          : 'no'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setLocal(
                        'hasOutstandingBalance',
                        v === 'all' ? undefined : v === 'yes'
                      );
                    }}
                  >
                    <StyledFormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All clients"
                    />
                    <StyledFormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Has outstanding balance"
                    />
                    <StyledFormControlLabel
                      value="no"
                      control={<Radio />}
                      label="Fully paid up"
                    />
                  </RadioGroup>
                </StyledAccordionDetails>
              </Accordion>
            </>
          )}

          {showPetFilters && (
            <>
              <Accordion defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <AccordionSectionTitle>Ownership</AccordionSectionTitle>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <RadioGroup
                    value={
                      localState.stillOwned === undefined
                        ? 'all'
                        : localState.stillOwned
                          ? 'yes'
                          : 'no'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setLocal(
                        'stillOwned',
                        v === 'all' ? undefined : v === 'yes'
                      );
                    }}
                  >
                    <StyledFormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All pets"
                    />
                    <StyledFormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Still owned"
                    />
                    <StyledFormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No longer owned"
                    />
                  </RadioGroup>
                </StyledAccordionDetails>
              </Accordion>

              {petTypes.length > 0 && (
                <Accordion defaultExpanded>
                  <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <AccordionSectionTitle>Pet Type</AccordionSectionTitle>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    <FormGroup>
                      {petTypes
                        .filter((pt) => !pt.isDeleted)
                        .map((petType) => (
                          <StyledFormControlLabel
                            key={petType.id}
                            control={
                              <Checkbox
                                checked={(localState.petTypeIds || []).includes(
                                  petType.id
                                )}
                                onChange={() => handlePetTypeToggle(petType.id)}
                              />
                            }
                            label={petType.type}
                          />
                        ))}
                    </FormGroup>
                  </StyledAccordionDetails>
                </Accordion>
              )}
            </>
          )}

          <Accordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <AccordionSectionTitle>Sort By</AccordionSectionTitle>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <RadioGroup
                value={localState.sortBy || 'default'}
                onChange={(e) => {
                  const v = e.target.value;
                  setLocal('sortBy', v === 'default' ? undefined : v);
                }}
              >
                <StyledFormControlLabel
                  value="default"
                  control={<Radio />}
                  label={
                    beingType === 'clients'
                      ? 'Surname (default)'
                      : 'Name (default)'
                  }
                />
                {showClientFilters && (
                  <StyledFormControlLabel
                    value="firstname"
                    control={<Radio />}
                    label="First Name"
                  />
                )}
                {showPetFilters && (
                  <>
                    <StyledFormControlLabel
                      value="type"
                      control={<Radio />}
                      label="Pet Type"
                    />
                    <StyledFormControlLabel
                      value="owner"
                      control={<Radio />}
                      label="Owner Name"
                    />
                  </>
                )}
                <StyledFormControlLabel
                  value="created"
                  control={<Radio />}
                  label="Date Created"
                />
                <StyledFormControlLabel
                  value="updated"
                  control={<Radio />}
                  label="Last Updated"
                />
              </RadioGroup>

              <Divider
                sx={{ my: 2, border: `2px solid ${theme.palette.grey[400]}` }}
              />

              <RadioGroup
                value={localState.sortOrder || 'asc'}
                onChange={(e) =>
                  setLocal('sortOrder', e.target.value as 'asc' | 'desc')
                }
              >
                <StyledFormControlLabel
                  value="asc"
                  control={<Radio />}
                  label="Ascending (A-Z)"
                />
                <StyledFormControlLabel
                  value="desc"
                  control={<Radio />}
                  label="Descending (Z-A)"
                />
              </RadioGroup>
            </StyledAccordionDetails>
          </Accordion>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 2,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClear}
            sx={{
              flex: 1,
              borderRadius: '2px',
              fontSize: '1.6rem',
              lineHeight: '1.8rem',
            }}
          >
            Reset
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApply}
            sx={{
              flex: 1,
              borderRadius: '2px',
              fontSize: '1.6rem',
              lineHeight: '1.8rem',
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
